/**
 * Single-step (flat) form component generator
 */

import { getAllFormSchemaFields } from '@core-engine/schema/field-helpers/index.js'
import type { FormSchema, GeneratorOptions } from '@core-engine/schema/types.js'
import { toPascalCase } from '@core-engine/utils/string.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths.js'
import {
  buildDefaultValues,
  buildFieldArrayDecls,
  buildFieldErrorHelper,
  buildWatchDecls,
  buildZodFields,
  escapeJsx,
  getListFields,
  type PublicFormComponentSourceOptions,
  renderFieldsJSX,
  type StepResult
} from '@next-adapter/generators/form-pipeline/form-component-shared.js'

export function generateSingleStepForm(
  _schema: FormSchema,
  _cwd: string,
  _adminDir: string,
  _options: GeneratorOptions
): StepResult {
  return { files: [] }
}

export function buildSingleStepFormComponentSource(
  schema: FormSchema,
  options: PublicFormComponentSourceOptions = {}
): string {
  const formName = schema.name
  const pascal = toPascalCase(formName)
  const fields = getAllFormSchemaFields(schema)
  const actionImportPath =
    options.actionImportPath ?? resolveSchemaActionImportPath(formName, options.outputNamespace)

  const zodFields = buildZodFields(fields)
  const defaults = buildDefaultValues(fields)
  const listFields = getListFields(fields)
  const hasListFields = listFields.length > 0
  const { setup: watchSetup } = buildWatchDecls(fields)

  const rawFields = schema.fields || []
  const fieldJSX = renderFieldsJSX(rawFields)

  const submitText = JSON.stringify(schema.submitButtonText || 'Submit')
  const successMessage = escapeJsx(schema.successMessage || 'Form submitted successfully!')
  const redirectUrl = schema.redirectUrl
  const successHandler = redirectUrl
    ? `window.location.href = ${JSON.stringify(redirectUrl)}`
    : 'setSubmitted(true)'

  const rhfImport = hasListFields
    ? `import { Controller, useFieldArray, useForm, type FieldErrors } from 'react-hook-form'`
    : `import { Controller, useForm, type FieldErrors } from 'react-hook-form'`

  const fieldArraySetup = hasListFields ? `\n${buildFieldArrayDecls(listFields)}\n` : ''

  const lucideImport = hasListFields ? `\nimport { Trash2 } from 'lucide-react'` : ''

  return `'use client'

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'${lucideImport}
import * as React from 'react'
${rhfImport}
import { z } from 'zod/v3'
import { create${pascal}Submission } from '@admin/actions/${actionImportPath}'

const formSchema = z.object({
${zodFields}
})

type FormValues = z.infer<typeof formSchema>
${buildFieldErrorHelper()}

export function ${pascal}Form() {
  const [submitted, setSubmitted] = React.useState(false)
  const [submitting, startSubmitTransition] = React.useTransition()

  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(formSchema),
    defaultValues: {
${defaults}
    },
  })
${fieldArraySetup}${watchSetup}
  function onSubmit(values: FormValues) {
    startSubmitTransition(async () => {
      try {
        const result = await create${pascal}Submission(values)
        if (result.success) {
          ${successHandler}
        } else {
          form.setError('root', { message: result.error || 'Something went wrong' })
        }
      } catch {
        form.setError('root', { message: 'Something went wrong. Please try again.' })
      }
    })
  }

  if (submitted) {
    return (
      <div className="rounded-sm border p-6 text-center">
        <h3 className="text-lg font-semibold">Thank you!</h3>
        <p className="mt-2 text-muted-foreground">${successMessage}</p>
      </div>
    )
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
${fieldJSX}

      {form.formState.errors.root && (
        <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-xs transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
      >
        {submitting ? 'Submitting...' : ${submitText}}
      </button>
    </form>
  )
}
`
}
